import React, { useState, useEffect } from 'react';
import { FiPlus, FiX } from 'react-icons/fi';
import client from '../api/client';

const MED_TYPES = ['PAIN', 'ANTIBIOTIC', 'STOOL_SOFTENER', 'ANTI_NAUSEA', 'OTHER'];

export default function PostOpMedicationList() {
  const [meds, setMeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', dosage: '', frequency: '', medicationType: 'PAIN', notes: '' });
  const [saving, setSaving] = useState(false);

  const fetchMeds = async () => {
    try {
      const res = await client.get('/recovery/medications');
      setMeds(res.data || []);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMeds(); }, []);

  const handleAdd = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await client.post('/recovery/medications', {
        ...form,
        startDate: new Date().toISOString().split('T')[0],
        isActive: true,
      });
      setForm({ name: '', dosage: '', frequency: '', medicationType: 'PAIN', notes: '' });
      setShowForm(false);
      fetchMeds();
    } catch {
      alert('Failed to add medication.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (id) => {
    try {
      await client.patch(`/recovery/medications/${id}/deactivate`);
      fetchMeds();
    } catch {
      alert('Failed to deactivate medication.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white" />
      </div>
    );
  }

  const activeMeds = meds.filter(m => m.isActive);
  const pastMeds = meds.filter(m => !m.isActive);

  const typeBadge = (type) => {
    const colors = {
      PAIN: 'bg-red-500/20 text-red-300',
      ANTIBIOTIC: 'bg-blue-500/20 text-blue-300',
      STOOL_SOFTENER: 'bg-yellow-500/20 text-yellow-300',
      ANTI_NAUSEA: 'bg-green-500/20 text-green-300',
      OTHER: 'bg-white/10 text-white/60',
    };
    return colors[type] || colors.OTHER;
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-medium text-white/60">Active Medications</h3>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-1 text-sm text-primary-400 hover:text-primary-300 transition"
        >
          <FiPlus size={14} /> Add
        </button>
      </div>

      {showForm && (
        <div className="bg-white/5 rounded-xl p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input
              type="text"
              placeholder="Medication name"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              className="rounded-lg bg-white/10 border-white/20 text-white placeholder-white/40 px-3 py-2 text-sm"
            />
            <input
              type="text"
              placeholder="Dosage (e.g. 500mg)"
              value={form.dosage}
              onChange={e => setForm({ ...form, dosage: e.target.value })}
              className="rounded-lg bg-white/10 border-white/20 text-white placeholder-white/40 px-3 py-2 text-sm"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input
              type="text"
              placeholder="Frequency (e.g. every 6 hours)"
              value={form.frequency}
              onChange={e => setForm({ ...form, frequency: e.target.value })}
              className="rounded-lg bg-white/10 border-white/20 text-white placeholder-white/40 px-3 py-2 text-sm"
            />
            <select
              value={form.medicationType}
              onChange={e => setForm({ ...form, medicationType: e.target.value })}
              className="rounded-lg bg-white/10 border-white/20 text-white px-3 py-2 text-sm"
            >
              {MED_TYPES.map(t => (
                <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleAdd}
              disabled={saving || !form.name.trim()}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 transition"
            >
              {saving ? 'Adding...' : 'Add Medication'}
            </button>
            <button
              onClick={() => setShowForm(false)}
              className="px-4 py-2 bg-white/10 text-white/60 rounded-lg text-sm hover:bg-white/20 transition"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {activeMeds.length === 0 && !showForm && (
        <p className="text-white/30 text-sm text-center py-4">No active post-op medications</p>
      )}

      {activeMeds.map(med => (
        <div key={med.id} className="flex items-center gap-3 p-3 bg-white/5 rounded-xl">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="text-white font-medium text-sm">{med.name}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs ${typeBadge(med.medicationType)}`}>
                {(med.medicationType || '').replace(/_/g, ' ')}
              </span>
            </div>
            <p className="text-xs text-white/40 mt-0.5">
              {med.dosage && `${med.dosage} · `}{med.frequency && `${med.frequency} · `}Since {med.startDate}
            </p>
          </div>
          <button
            onClick={() => handleDeactivate(med.id)}
            className="text-xs text-white/30 hover:text-red-400 transition"
          >
            Discontinue
          </button>
        </div>
      ))}

      {pastMeds.length > 0 && (
        <>
          <h3 className="text-sm font-medium text-white/40 mt-6">Past Medications</h3>
          {pastMeds.map(med => (
            <div key={med.id} className="flex items-center gap-3 p-3 bg-white/5 rounded-xl opacity-60">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-white/70 font-medium text-sm">{med.name}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs ${typeBadge(med.medicationType)}`}>
                    {(med.medicationType || '').replace(/_/g, ' ')}
                  </span>
                </div>
                <p className="text-xs text-white/30 mt-0.5">
                  {med.startDate} &mdash; {med.endDate || 'unknown'}
                </p>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
